/**
 * Hook Testing Utilities
 * 
 * This module provides utilities for testing React hook execution order and behavior.
 * It helps identify potential issues with hook execution patterns across different 
 * component states and rendering conditions.
 */

import { useRef, useEffect, useMemo } from 'react';

/**
 * Interface for the hook consistency check API
 */
export interface HookConsistencyApi {
  /**
   * Records a hook execution
   * @param hookId Unique identifier for the hook
   */
  recordHook: (hookId: string) => void;
  
  /**
   * Gets the current execution patterns
   */
  getExecutionPatterns: () => string[];
  
  /**
   * Checks if hook execution has been consistent across renders
   */
  hasConsistentExecution: () => boolean;
  
  /**
   * Gets any detected hook rule violations
   */
  getViolations: () => string[];
}

/**
 * Creates a context for testing hook execution across a component
 * @returns Utility functions for tracking and verifying hook execution
 */
export function createHookExecutionContext() {
  // Store for hook execution orders across renders
  const executionStore: string[] = [];
  const executionLogs: string[][] = [];
  let executionCheckEnabled = true;
  
  return {
    /**
     * Records a hook execution
     * @param hookId Unique identifier for the hook
     */
    recordExecution: (hookId: string) => {
      if (executionCheckEnabled) {
        executionStore.push(hookId);
      }
    },
    
    /**
     * Checks if there are any inconsistencies in hook execution order
     * across renders
     * @returns Whether the execution has inconsistencies
     */
    hasInconsistentExecution: () => {
      // We need to track executions separately
      if (!executionStore || executionStore.length === 0) {
        return false;
      }
      
      // Get the unique hook IDs (excluding cleanup events)
      const hookIds = [...new Set(executionStore.filter(id => !id.includes(':cleanup')))];      
      const hookExecutions: Record<string, number> = {};
      
      // Count occurrences of each hook
      for (const hookId of hookIds) {
        hookExecutions[hookId] = executionStore.filter(id => id === hookId).length;
      }
      
      // Check if any hook has inconsistent execution count
      const executionCounts = Object.values(hookExecutions);
      if (executionCounts.length > 0) {
        const firstCount = executionCounts[0];
        
        // If any hook has a different count, we have inconsistencies
        for (let i = 1; i < executionCounts.length; i++) {
          if (executionCounts[i] !== firstCount) {
            return true;
          }
        }
      }
      
      // Log the current execution for future comparisons
      executionLogs.push([...executionStore]);
      
      return false;
    },
    
    /**
     * Verifies that hooks are executed in a specific order
     * @param expectedOrder The expected order of hook executions
     * @returns Whether the hooks executed in the expected order
     */
    verifyExecutionOrder: (expectedOrder?: string[]) => {
      if (!executionStore || executionStore.length === 0) {
        return false;
      }
      
      if (expectedOrder && expectedOrder.length > 0) {
        if (executionStore.length !== expectedOrder.length) {
          console.error(
            `Hook execution count mismatch: Expected ${expectedOrder.length} hooks, found ${executionStore.length}`
          );
          return false;
        }
        
        // Find the first mismatch in order
        const mismatchIndex = expectedOrder.findIndex(
          (id, index) => executionStore[index] !== id
        );
        
        if (mismatchIndex >= 0) {
          console.error(
            `Hook execution order mismatch at position ${mismatchIndex}: Expected ${expectedOrder[mismatchIndex]} but found ${executionStore[mismatchIndex]}`
          );
          return false;
        }
        
        return true;
      }
      
      // No expected order provided, so we just return true
      return true;
    },
    
    /**
     * Gets the current execution order of all hooks
     * @returns Array of hook IDs in execution order
     */
    getExecutionOrder: () => [...executionStore],
    
    /**
     * Resets the execution tracking
     */
    reset: () => {
      executionStore.length = 0;
      executionLogs.length = 0;
    },
    
    /**
     * Enables or disables execution checking
     * @param enabled Whether to enable execution checking
     */
    setExecutionCheckEnabled: (enabled: boolean) => {
      executionCheckEnabled = enabled;
    },
    
    /**
     * Gets any detected hook rule violations
     * @returns Array of violation messages
     */
    getViolations: () => {
      const violations: string[] = [];
      
      // Check for inconsistent hook counts between renders
      if (executionLogs.length > 1) {
        const baselineCount = executionLogs[0].length;
        
        for (let i = 1; i < executionLogs.length; i++) {
          const currentCount = executionLogs[i].length;
          
          if (currentCount !== baselineCount) {
            violations.push(
              `Inconsistent hook count: Expected ${baselineCount} hooks, found ${currentCount} in render #${i+1}.`
            );
          }
          
          // Check for mismatches in hook order
          for (let j = 0; j < Math.min(baselineCount, currentCount); j++) {
            if (executionLogs[0][j] !== executionLogs[i][j]) {
              violations.push(`Hook order mismatch at position ${j}: expected "${executionLogs[0][j]}", got "${executionLogs[i][j]}"`);
              break;
            }
          }
        }
      }
      return violations;
    }
  };
}

/**
 * Tracks and validates the execution order of hooks in a component
 * @param hookId Unique identifier for the hook (e.g., component name + hook name)
 * @param executionStore Reference to shared execution store object
 * @returns Object with isValid and getExecutionOrder
 */
export function useHookExecutionTracker(
  hookId: string,
  executionStore: React.MutableRefObject<string[]>,
) {
  // Track whether this is the initial mount
  const isInitialRender = useRef(true);
  
  // Track this hook's position in the overall execution order
  const position = useRef<number>(-1);
  
  // Record this hook's execution
  if (isInitialRender.current) {
    // First render - record the position
    position.current = executionStore.current.length;
    executionStore.current.push(hookId);
  } else {
    // Subsequent renders - verify position matches
    if (executionStore.current[position.current] !== hookId) {
      console.error(
        `Hook execution order violation: ${hookId} expected at position ${position.current} but found ${executionStore.current[position.current]}`
      );
    }
  }
  
  // Use a special effect to mark the end of the initial render
  useEffect(() => {
    isInitialRender.current = false;
    
    // Add a cleanup marker
    return () => {
      // Mark cleanup execution
      executionStore.current.push(`${hookId}:cleanup`);
    };
  }, [executionStore, hookId]);
  
  return {
    isValid: true,
    getExecutionOrder: () => [...executionStore.current],
  };
}

/**
 * Hook to verify that hooks execute consistently across renders
 * @param componentName Name of the component being tested
 * @param stateName Name of the particular state being measured (optional)
 * @returns API for recording and checking hook execution
 */
export function useHookConsistencyCheck(
  componentName: string,
  stateName = 'default'
): HookConsistencyApi {
  // Store data about hook executions
  const hookData = useRef({
    executions: [] as string[],
    previousExecutions: [] as string[][],
    violations: [] as string[],
    hasInconsistencies: false,
  });
  
  // Create and memoize the API
  const api = useMemo(() => {
    return {
      /**
       * Records a hook being executed
       */
      recordHook: (hookId: string) => {
        hookData.current.executions.push(`${hookId}`);
      },
      
      /**
       * Gets the current execution patterns
       */
      getExecutionPatterns: () => [...hookData.current.executions],
      
      /**
       * Checks if hook execution is consistent
       */
      hasConsistentExecution: () => {
        // If we haven't recorded a previous execution yet, store this one
        if (hookData.current.previousExecutions.length === 0) {
          hookData.current.previousExecutions.push([...hookData.current.executions]);
          return true;
        }
        
        // Compare against the first recorded execution
        const baseline = hookData.current.previousExecutions[0];
        const current = hookData.current.executions;
        
        // Check for length mismatch (different number of hooks called)
        if (baseline.length !== current.length) {
          hookData.current.violations.push(
            `Hook count mismatch in ${componentName} (${stateName}): Expected ${baseline.length} hooks but found ${current.length}. ` +
            `This likely indicates conditional hook calls, which violates React's rules of hooks.`
          );
          hookData.current.hasInconsistencies = true;
          return false;
        }
        
        // Check for order mismatch
        for (let i = 0; i < baseline.length; i++) {
          if (baseline[i] !== current[i]) {
            hookData.current.violations.push(
              `Hook order mismatch in ${componentName} (${stateName}) at position ${i}: ` +
              `Expected "${baseline[i]}" but found "${current[i]}". ` +
              `This violates React's rules of hooks requiring hooks to be called in the same order.`
            );
            hookData.current.hasInconsistencies = true;
            return false;
          }
        }
        
        // Store this execution for future comparisons
        hookData.current.previousExecutions.push([...current]);
        
        // Clear executions for next render cycle
        hookData.current.executions = [];
        
        return true;
      },
      
      /**
       * Gets all detected violations
       */
      getViolations: () => hookData.current.violations
    };
  }, [componentName, stateName]);
  
  return api;
}
